import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import { Breadcrumb, Input, Table, Space, Popconfirm, Row } from "antd";
import { getRequest, postRequest } from "../../../axios";
import { ReactComponent as Archive } from "../../../image/Archive.svg";
import ButtonField from "../../../common/htmlComponents/ButtonField";
import SearchIcon from "../../../image/SearchIcon.svg";
import { ReactComponent as ArchiveRestore } from "../../../image/ArchiveRestore.svg";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";
import "antd/dist/antd.min.css";
import "../../../styles/Main.scss";
import { RestFilled } from "@ant-design/icons";
import moment from "moment";

const { Search } = Input;

const ArchivedCommercialInvoiceList = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [archivedCommercialInvoiceList, setArchivedCommercialInvoiceList] = useState([]);
  const [totalCount, settotalCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState([]);
  const [reload, setReload] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [state, setState] = useState({
    page: 1,
    ordering: "id",
    pagesize: 10,
    search: "",
    total: 0,
  });

  useEffect(() => {
    const getPurchasedSubscriptionArchiveList = async () => {
      setLoading(true);

      try {
        let apiResponse = await getRequest("commercial-invoice-archive/", state);
        if (apiResponse.data.results.success) {
          setArchivedCommercialInvoiceList(apiResponse.data.results.data);
          settotalCount(apiResponse.data.count);
        } else {
          ErrorNotificationMsg("Error in archived material list");
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    getPurchasedSubscriptionArchiveList();
  }, [state, reload]);

  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id";

    if (sortField === "Client" && sortOrder === "ascend") {
      sortingEnumKey = "Client";
    }
    if (sortField === "Client" && sortOrder === "descend") {
      sortingEnumKey = "-Client";
    }
    if (sortField === "subscription" && sortOrder === "ascend") {
      sortingEnumKey = "subscription";
    }
    if (sortField === "subscription" && sortOrder === "descend") {
      sortingEnumKey = "-subscription";
    }

    if (sortField === "payment_mode" && sortOrder === "ascend") {
      sortingEnumKey = "payment_mode";
    }
    if (sortField === "payment_mode" && sortOrder === "descend") {
      sortingEnumKey = "-payment_mode";
    }

    if (sortField === "active" && sortOrder === "ascend") {
      sortingEnumKey = "active";
    }
    if (sortField === "active" && sortOrder === "descend") {
      sortingEnumKey = "-active";
    }

    if (sortField === "start_date" && sortOrder === "ascend") {
      sortingEnumKey = "start_date";
    }
    if (sortField === "start_date" && sortOrder === "descend") {
      sortingEnumKey = "-start_date";
    }

    if (sortField === "end_date" && sortOrder === "ascend") {
      sortingEnumKey = "end_date";
    }
    if (sortField === "end_date" && sortOrder === "descend") {
      sortingEnumKey = "-end_date";
    }

    return sortingEnumKey;
  };

  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData(value, onSearch);
  };
  const onSearch = (value) => {
    setState({
      ...state,
      search: value,
    });
  };

  const debounceLoadData = debounce((value, onSearch) => {
    onSearch(value);
  }, 1000);
  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedRow(selectedRowKeys);
    },
  };

  const handleRestore = async (id = "") => {
    let ids = selectedRow;
    if (ids.length === 0) {
      ids.push(id);
    }
    try {
      let apiResponse = await postRequest("commercial-invoice-restore/", {
        deleted: ids,
      });
      if (apiResponse.data.success) {
        SuccessNotificationMsg("Commercial Invoice restored successfully");
        setReload(!reload);
        setSelectedRow([]);
      } else {
        ErrorNotificationMsg("Error in restored Commercial Invoice");
      }
    } catch (error) {
      ErrorNotificationMsg(error);
    }
  };

  const columns = [
    {
      title: "Client",
      dataIndex: "billed_to_name",
      key: "billed_to_name",
      sorter: true,
    },
    {
      title: "Invoice No.",
      dataIndex: "invoice_number",
      key: "invoice_number",
      sorter: true,
    },
    {
      title: "Invoice Date",
      dataIndex: "invoice_date",
      key: "invoice_date",
      sorter: true,
      render: (date) => date ? moment(date).format("DD-MM-YYYY") : "-",
    },
    {
      title: "Total (INR)",
      dataIndex: "grand_total",
      key: "grand_total",
      sorter: true,
      render: (value) => value?.toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
      }),
    },

    {
      title: "ACTION",
      key: "operation",
      render: (text, record) => (
        <Space>
          <Popconfirm
            className="action"
            title={t("Are you sure to restored ?")}
            okText={t("pop_ok")}
            cancelText={t("pop_cancel")}
            onConfirm={() => handleRestore(record.id)}
          >
            <ArchiveRestore style={{ cursor: "pointer", width:"60%"}} />{" "}
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3> {t("Archived Commercial Invoice List")}</h3>

            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/commercial-invoice">
                  {t("commercialInvoiceList")}
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("archiveList")}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Space>
            <div className="searchwrp">
              <Space direction="vertical">
              <Search
  value={searchText}
  onChange={(e) => {
    const value = e.target.value;
    setSearchText(value);
    debounceLoadData(value, onSearch);
  }}
  allowClear
  onSearch={(value) => {
    setSearchText(value);
    onSearch(value);
  }}
  prefix={
    <img
      src={SearchIcon}
      alt="SearchIcon"
      className="SearchIcon"
      width={22}
    />
  }
  placeholder={t("pl_search_list")}
  style={{ width: 250 }}
/>

              </Space>
            </div>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_restore_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleRestore()}
              >
                <ButtonField
                  className="BlueShadowBtn"
                  type="primary"
                  htmlType="button"
                  label="Restored"
                />
              </Popconfirm>
            )}
          </Space>
        </div>
      </div>

      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body">
          <Row className="tblheader">
            {/* <Col xs={24} sm={24} md={12} lg={12} xl={12} className="tbl-heading">
            <h1>
           
              {t("Archived Commercial Invoice List")}
            </h1>
          </Col> */}
            {/* <Col xs={24} sm={24} md={12} lg={12} xl={12} className="tbl-action">
            <div className="searchwrp">
              <Space direction="vertical">
                <Search
                  prefix={
                    <img
                      src={SearchIcon}
                      alt="SearchIcon"
                      className="SearchIcon"
                      width={22}
                    />
                  }
                  placeholder={t("pl_search_list")}
                  onChange={searchCallbackDelayed}
                  style={{
                    width: 200,
                  }}
                />
              </Space>
            </div>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_restore_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleRestore()}
              >
                <ButtonField
                  className="BlueShadowBtn"
                  type="primary"
                  htmlType="button"
                  label="Restore"
                />
              </Popconfirm>
            )}
          </Col> */}
          </Row>
<div className="tablewrp">
          <Table
            rowSelection={rowSelection}
            rowKey={(record) => record.id}
            columns={columns}
            dataSource={archivedCommercialInvoiceList}
            scroll={{ x: 800 }}
            loading={loading}
            pagination={{
              defaultCurrent: state.page,
              defaultPageSize: state.pagesize,
              total: totalCount,
              showSizeChanger: true,
              showTotal: (totalCount) => `Total ${totalCount} items`,
            }}
            onChange={handleTableChange}
          />
</div>
        </div>
      </section>
    </>
  );
};

export default ArchivedCommercialInvoiceList;
